import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Button,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  StepConnector,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Assignment as TaskIcon,
  Description as DocumentIcon,
  Gavel as RegulationIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  Schedule as ClockIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { ComplianceReport as ComplianceReportType } from '../../services/complianceService';
import RiskMeter from './RiskMeter';
import SanctionsDetailCard from './SanctionsDetailCard';

interface ComplianceReportProps {
  report: ComplianceReportType;
}

const ComplianceReport: React.FC<ComplianceReportProps> = ({ report }) => {
  const theme = useTheme();

  // Export functions
  const exportToPDF = (report: ComplianceReportType) => {
    // Generate filename
    const filename = `compliance-report-${report.report_id}-${new Date().toISOString().split('T')[0]}.pdf`;

    // In a real implementation, this would use a library like jsPDF or html2pdf
    // For now, we'll trigger the browser's print dialog
    window.print();

    console.log('PDF export triggered for:', filename);
  };

  const exportToJSON = (report: ComplianceReportType) => {
    // Generate filename
    const filename = `compliance-report-${report.report_id}-${new Date().toISOString().split('T')[0]}.json`;

    // Convert report to JSON
    const jsonString = JSON.stringify(report, null, 2);

    // Create blob and download
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportToCSV = (report: ComplianceReportType) => {
    // Generate filename
    const filename = `compliance-report-${report.report_id}-${new Date().toISOString().split('T')[0]}.csv`;

    // Build CSV content
    let csvContent = 'Field,Value\n';
    csvContent += `Report ID,${report.report_id}\n`;
    csvContent += `Created At,${new Date(report.created_at).toLocaleString()}\n`;
    csvContent += `Overall Status,${report.overall_status}\n`;
    csvContent += `Risk Score,${report.risk_assessment?.risk_score || report.risk_assessment?.overall_risk_score || 0}\n`;
    csvContent += `Risk Level,${report.risk_assessment?.risk_level || 'N/A'}\n`;
    csvContent += `Processing Time,${report.processing_time_ms || report.check_duration_ms || 0}ms\n`;

    // Add sanctions check results
    if (report.sanctions_check && report.sanctions_check.length > 0) {
      csvContent += '\nSanctions Check Results\n';
      csvContent += 'Entity Name,Entity Type,Is Sanctioned,Match Score,Threshold,Fuzzy Matching,Lists Checked,Matched Lists,Match Count,Search ID,API Version,Credential Source,Checked At\n';
      report.sanctions_check.forEach((result: any) => {
        csvContent += `"${result.entity_name}",${result.entity_type},${result.is_sanctioned},${result.match_score || 0},`;
        csvContent += `${result.threshold_used || 'N/A'},${result.fuzzy_matching_used ? 'Yes' : 'No'},`;
        csvContent += `"${result.lists_checked?.join('; ') || 'N/A'}","${result.matched_lists?.join('; ') || 'None'}",`;
        csvContent += `${result.match_count || 0},${result.search_id || 'N/A'},${result.api_version || 'N/A'},`;
        csvContent += `${result.credential_source || 'N/A'},"${result.checked_at}"\n`;

        // Add individual match details if available
        if (result.matches && result.matches.length > 0) {
          csvContent += '\n,Match Details\n';
          csvContent += ',Match #,Name,Score,List,Reason\n';
          result.matches.forEach((match: any, idx: number) => {
            csvContent += `,${idx + 1},"${match.name || 'N/A'}",${match.score || 0},"${match.list || 'N/A'}","${match.reason || 'N/A'}"\n`;
          });
          csvContent += '\n';
        }
      });
    }

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getStatusColor = (): 'success' | 'error' | 'warning' | 'info' => {
    switch (report.overall_status) {
      case 'compliant':
        return 'success';
      case 'non_compliant':
        return 'error';
      case 'requires_review':
        return 'warning';
      case 'conditional':
        return 'info';
      default:
        return 'info';
    }
  };

  const getStatusIcon = () => {
    switch (report.overall_status) {
      case 'compliant':
        return <CheckIcon />;
      case 'non_compliant':
        return <ErrorIcon />;
      case 'requires_review':
        return <WarningIcon />;
      default:
        return <SecurityIcon />;
    }
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  return (
    <Box>
      {/* Report Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              Compliance Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Report ID: {report.report_id} | Generated: {new Date(report.created_at).toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => exportToPDF(report)}
            >
              Export PDF
            </Button>
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => exportToJSON(report)}
            >
              Export JSON
            </Button>
            <Button
              size="small"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
            >
              Print
            </Button>
            <Button size="small" startIcon={<ShareIcon />}>Share</Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Overall Status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            icon={getStatusIcon()}
            label={(report.overall_status || 'pending').replace('_', ' ').toUpperCase()}
            color={getStatusColor()}
            sx={{ fontWeight: 'bold' }}
          />
          <Typography variant="body2" color="text.secondary">
            Processing Time: {formatDuration(report.processing_time_ms || report.check_duration_ms || 0)}
          </Typography>
        </Box>

        {/* Summary */}
        <Alert severity={getStatusColor()} sx={{ mt: 2 }}>
          <Typography variant="body2">
            {report.summary}
          </Typography>
        </Alert>
      </Paper>

      <Grid container spacing={3}>
        {/* Risk Assessment */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Risk Assessment
            </Typography>
            <RiskMeter score={report.risk_assessment?.risk_score || report.risk_assessment?.overall_risk_score || 0} />
            
            {/* Highest Risks */}
            {report.risk_assessment?.highest_risks?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Highest Risk Areas
                </Typography>
                <List dense>
                  {report.risk_assessment?.highest_risks?.map((risk, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <WarningIcon color="warning" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={risk} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Required Actions */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Required Actions ({report.required_actions?.length || 0})
            </Typography>
            
            {report.required_actions?.length > 0 ? (
              <Stepper orientation="vertical">
                {report.required_actions?.map((action, index) => (
                  <Step key={index} active={true} completed={false}>
                    <StepLabel
                      StepIconComponent={() => (
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            bgcolor:
                              action.priority === 'CRITICAL' ? theme.palette.error.main :
                              action.priority === 'HIGH' ? theme.palette.warning.main : theme.palette.primary.main,
                            color: theme.palette.common.white,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <TaskIcon />
                        </Box>
                      )}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2">
                          {action.action_type}
                        </Typography>
                        {action.estimated_time && (
                          <Typography variant="caption" color="text.secondary">
                            <ClockIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                            {action.estimated_time}
                          </Typography>
                        )}
                      </Box>
                    </StepLabel>
                    <StepContent>
                      <Typography variant="body2" gutterBottom>
                        {action.description}
                      </Typography>
                      {action.responsible_party && (
                        <Typography variant="caption" color="text.secondary">
                          Responsible: {action.responsible_party}
                        </Typography>
                      )}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            ) : (
              <Alert severity="success">
                No compliance actions required
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Sanctions Check Results */}
        {report.sanctions_check && report.sanctions_check.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Sanctions Screening Results
              </Typography>

              {/* Summary Statistics */}
              <Paper
                sx={{
                  p: 2,
                  mb: 3,
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.common.white,
                  boxShadow: 3,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="h4" fontWeight="bold">
                      {report.sanctions_check.length}
                    </Typography>
                    <Typography variant="body2">Entities Checked</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="h4" fontWeight="bold" sx={{ color: theme.palette.error.light }}>
                      {report.sanctions_check.filter((r: any) => r.is_sanctioned).length}
                    </Typography>
                    <Typography variant="body2">Sanctioned Entities</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="h4" fontWeight="bold">
                      {report.sanctions_check[0]?.lists_checked?.length || 0}
                    </Typography>
                    <Typography variant="body2">Lists Screened</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="h4" fontWeight="bold">
                      {report.sanctions_check[0]?.api_version || 'N/A'}
                    </Typography>
                    <Typography variant="body2">API Version</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Detailed Results */}
              <Grid container spacing={2}>
                {report.sanctions_check.map((result, index) => (
                  <Grid item xs={12} key={index}>
                    <SanctionsDetailCard result={result} index={index} />
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* License Requirements */}
        {report.license_check && report.license_check.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                License Requirements
              </Typography>
              <List>
                {report.license_check.map((license, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <DocumentIcon color={license.is_required ? 'error' : 'action'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={license.license_type}
                      secondary={
                        <Box>
                          <Typography variant="caption" display="block">
                            Jurisdiction: {license.jurisdiction} | Category: {license.product_category}
                          </Typography>
                          {license.is_required && (
                            <Typography variant="caption" color="error">
                              Required - Processing Time: {license.processing_time_days} days
                            </Typography>
                          )}
                          {license.exemptions && license.exemptions.length > 0 && (
                            <Typography variant="caption" sx={{ color: theme.palette.success.main }}>
                              Exemptions available: {license.exemptions.join(', ')}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        )}

        {/* Mitigation Measures */}
        {report.risk_assessment?.mitigation_measures?.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Mitigation Measures
              </Typography>
              <List>
                {report.risk_assessment?.mitigation_measures?.map((measure, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={measure} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        )}

        {/* Recommendations */}
        {report.recommendations && report.recommendations.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Recommendations
              </Typography>
              <List>
                {report.recommendations.map((recommendation, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <RegulationIcon color="info" />
                    </ListItemIcon>
                    <ListItemText primary={recommendation} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        )}

        {/* Required Documents */}
        {report.required_documents?.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Required Documentation
              </Typography>
              <Grid container spacing={2}>
                {report.required_documents.map((doc, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DocumentIcon color="action" />
                          <Typography variant="body2">
                            {doc}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Confidence Score */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Confidence Score: {(report.risk_assessment?.confidence_score || 0).toFixed(0)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {report.jurisdictions_checked?.length || 0} Jurisdictions | {report.regulations_checked?.length || 0} Regulations Checked
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ComplianceReport;